import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, catchError, concatMap, forkJoin, from, map, Observable, of, toArray } from 'rxjs';
import { conceptosDto, DatosFacturaCompleta } from '../models/carga-fc.dto';
import { OcConceptosService } from './oc-conceptos.service';
import { errorMessageHandler } from '../core/utils/error-handler.utils';

@Injectable({
  providedIn: 'root'
})
export class ComproCabeProveService {


  private apiUrl = ``;
  private entity = `compro-cabe-prove`;

  private datosFactura$ = new BehaviorSubject<DatosFacturaCompleta | null>(null);

  constructor(private http: HttpClient,
    private ocConceptosService: OcConceptosService
  ) {

    if (environment.production)
      this.apiUrl = environment.apiUrl + this.entity;
    else
      this.apiUrl = environment.apiUrlDev + this.entity;

  }

  getOCPendientes(filtros: { fecha_desde: string, fecha_hasta: string, moneda: string }): Observable<conceptosDto[]> {
    let params = new HttpParams();

    if (filtros.fecha_desde)
      params = params.set('fecha_desde', filtros.fecha_desde);

    if (filtros.fecha_hasta)
      params = params.set('fecha_hasta', filtros.fecha_hasta);

    if (filtros.moneda)
      params = params.set('moneda', filtros.moneda);

    return this.http.get<conceptosDto[]>(`${this.apiUrl}/oc-pendientes`, { params });
  }

  proponerNumeracion(tipo: string) {
    return this.http.get<[{ num: number }]>(`${this.apiUrl}/numeracion/${tipo}`);
  }


  setDatosFactura(data: DatosFacturaCompleta) {
    this.datosFactura$.next(data);
  }

  getDatosFactura$(): Observable<DatosFacturaCompleta | null> {
    return this.datosFactura$.asObservable();
  }

  clearDatosFactura() {
    this.datosFactura$.next(null);
  }

  completarDatosConceptos(datos: DatosFacturaCompleta): Observable<DatosFacturaCompleta> {
    if (!datos || !datos.conceptos?.length) {
      return of(datos);
    }

    return from(datos.conceptos).pipe(
      concatMap((concepto, index) =>
        this.ocConceptosService.getConcepto(concepto.TIPO, concepto.NUM, concepto.renglon).pipe(
          map(response => ({ response, index })),
          catchError(err => {
            console.error(`Error obteniendo concepto ${concepto.TIPO}-${concepto.NUM}-${concepto.renglon}`, err);
            return of({ response: null, index }); // Sigue con los demás
          })
        )
      ),
      toArray(), // Recolecta todos los resultados en un array
      map(respuestas => {
        respuestas.forEach(({ response, index }) => {
          const concepto = datos.conceptos[index];

          const conceptoActualizado = response;

          if (!conceptoActualizado) return;

          concepto.OBS2 = conceptoActualizado.OBS2;
          concepto.OBS3 = conceptoActualizado.OBS3;
          concepto.OBS4 = conceptoActualizado.OBS4;
          concepto.PRECE_TIPO = conceptoActualizado.TIPO;
          concepto.PRECE_NUM = conceptoActualizado.NUM;
          concepto.PRECE_ITEM = conceptoActualizado.RENGLON;
          concepto.CODI_ESTA = conceptoActualizado.CODI_ESTA;
          concepto.CODI_CENCO = conceptoActualizado.CODI_CENCO;
          concepto.USER_ANU = conceptoActualizado.USER_ANU;
          concepto.IMPOR_PEND_ANU = conceptoActualizado.IMPOR_PEND_ANU;
          concepto.IMPOR_NETO = conceptoActualizado.IMPOR_NETO;
          concepto.OBSER_CENCO = conceptoActualizado.OBSER_CENCO;
          concepto.OBSER_CENCO_2 = conceptoActualizado.OBSER_CENCO_2;
          concepto.descrip_cenco = conceptoActualizado.descrip_cenco;
          concepto.cuenta_concep = conceptoActualizado.cuenta_concep;
          concepto.impor_editable = concepto.impor_pend;
          concepto.porcen_iva_editable = concepto.porcen_iva;
        });

        return datos; // Devuelve el objeto con conceptos ya actualizados
      })
    );
  }

  grabarComprobante(datosFactura: DatosFacturaCompleta | null) {
    return this.http.post<any>(`${this.apiUrl}`, datosFactura);
  }

  enviarMailFactura(formData: FormData) {
    return this.http.post<{ mensaje: string; }>(`${this.apiUrl}/mail`, formData);
  }

  obtenerComprobantes(filtros: { fecha_desde: string, fecha_hasta: string, estado: string }): Observable<any[]> {
    let params = new HttpParams();
    if (filtros.fecha_desde)
      params = params.set('fecha_desde', filtros.fecha_desde);
    if (filtros.fecha_hasta)
      params = params.set('fecha_hasta', filtros.fecha_hasta);
    if (filtros.estado)
      params = params.set('estado', filtros.estado);
    return this.http.get<any[]>(`${this.apiUrl}`, { params });
  }



}
